/* @flow */
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { graphql, compose } from 'react-apollo';
import gql from 'graphql-tag';
import idx from 'idx';
import * as React from 'react';

import { showToast } from 'reducers/toasts';
import Button from 'components/Button';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import ConfirmPhone from 'auth/components/ConfirmPhone';
import ErrorReporter from 'app/components/ErrorReporter';
import Heading from 'components/Heading';
import Text from 'components/Text';

import GET_PENDING_SMS_DEVICES from 'app/queries/PendingSmsDevicesQuery.gql';

import type { PendingSmsDevices } from 'app/queries/__generated__/PendingSmsDevices';

import './styles/AddedDeviceVerifier.scss';

function NoRendererError() {
  return (
    <ErrorReporter error="AddedDeviceVerifier.no_suitable_renderer">
      <Heading>Oops, an error occured.</Heading>
    </ErrorReporter>
  );
}

function GraphqlLoadingError() {
  return (
    <ErrorReporter error="AddedDeviceVerifier.graphql_data_loading_failed">
      <Heading>
        Oops, we could not load the device. It may have already been removed from your account.
      </Heading>
    </ErrorReporter>
  );
}

type Props = {
  data: ReactApolloData<PendingSmsDevices>,
  pendingSmsDeviceId: Backbone$Id,
  onVerificationSuccess?: Function,
  onRejectionSuccess?: Function,
  rejectDevice: (pendingSmsDeviceId: Backbone$Id) => Promise<void>,
  showToast: (text: string, kind: 'success' | 'error') => *,
};

type Status =
  | 'device_confirmation'
  | 'device_removed'
  | 'inbound_verification'
  | 'inbound_verification_success';

type State = {
  status: Status,
};

class AddedDeviceVerifier extends React.PureComponent<Props, State> {
  state: State = {
    status: 'device_confirmation',
  };

  rejectDevice = () =>
    (async () => {
      try {
        await this.props.rejectDevice(this.props.pendingSmsDeviceId);
        if (this.props.onRejectionSuccess) {
          this.props.onRejectionSuccess();
        }
        this.props.showToast('Device removed from your accounts', 'success');
        this.setState({ status: 'device_removed' });
      } catch (error) {
        this.props.showToast(error.toString(), 'error');
        console.error('AddedDeviceVerifier - could not reject device', error);
      }
    })();

  initiateInboundVerification = () => {
    this.setState({ status: 'inbound_verification' });
  };

  inboundVerificationSuccess = () => {
    if (this.props.onVerificationSuccess) {
      this.props.onVerificationSuccess();
    }
    this.setState({ status: 'inbound_verification_success' });
  };

  render() {
    const { loading, me } = this.props.data;

    if (loading) {
      return <CenteredLoadingIndicator />;
    }

    if (this.state.status === 'device_removed') {
      return (
        <div>
          <Heading>The device was successfully removed from your account, thank you.</Heading>
        </div>
      );
    }

    const pendingSmsDevices = idx(me, _ => _.pendingSmsDevices);
    const pendingSmsDevice =
      pendingSmsDevices && pendingSmsDevices.find(d => d.id === this.props.pendingSmsDeviceId);

    if (!pendingSmsDevice) {
      return <GraphqlLoadingError />;
    }

    if (this.state.status === 'device_confirmation') {
      return (
        <div className="AddedDeviceVerifier AddedDeviceVerifier-phoneConfirmation">
          <Heading align="center">Is {pendingSmsDevice.label} your phone number?</Heading>
          <Text color="thunder">
            {pendingSmsDevice.addedByUser.name} added it to your Remind account.
          </Text>
          <div className="AddedDeviceVerifier-phoneConfirmation-actions">
            <div className="AddedDeviceVerifier-phoneConfirmation-buttons">
              <Button level="secondary" onClick={this.rejectDevice}>
                No
              </Button>
              <Button level="primary" onClick={this.initiateInboundVerification}>
                Yes
              </Button>
            </div>
          </div>
          <Text
            color="thunder"
            align="center"
            element="div"
            className="AddedDeviceVerifier-phoneConfirmation-footer">
            Verifying this device will not change the way you receive Remind messages. To change
            this, go to your notification settings.
          </Text>
        </div>
      );
    }

    if (this.state.status === 'inbound_verification') {
      return (
        <ConfirmPhone
          uid={pendingSmsDevice.phoneNumber}
          onSuccess={this.inboundVerificationSuccess}
        />
      );
    }

    if (this.state.status === 'inbound_verification_success') {
      return (
        <div>
          <Heading>The device has been succesfully verified, thank you.</Heading>
        </div>
      );
    }

    return (
      <div>
        <NoRendererError />
      </div>
    );
  }
}

const withPendingSmsDevices = graphql(GET_PENDING_SMS_DEVICES);

const REMOVE_PENDING_SMS_DEVICE = gql`
  mutation removePendingSmsDevice($id: Int!) {
    removePendingSmsDevice(id: $id) {
      error {
        message
        code
      }
    }
  }
`;

const withMutation = graphql(REMOVE_PENDING_SMS_DEVICE, {
  props: ({ mutate }) => ({
    rejectDevice: id =>
      mutate({
        variables: { id },
        updateQueries: {
          getPendingSmsDevices: prev => {
            return prev.me.pendingSmsDevices.filter(d => d.id !== id);
          },
        },
      }).then(response => {
        if (idx(response, _ => _.data.removeDevice.error)) {
          throw new Error(response.data.removeDevice.error.message);
        }
      }),
  }),
});

function mapDispatchToProps(dispatch: Function) {
  return bindActionCreators({ showToast }, dispatch);
}

export { AddedDeviceVerifier };

export default compose(
  connect(
    undefined,
    mapDispatchToProps
  ),
  withMutation,
  withPendingSmsDevices
)(AddedDeviceVerifier);
